import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Contact } from '../../../../sic-lib/types/Contact';
import { debounce } from '../../../../sic-lib/utils/throttlingHelpers';
import { useAppSelector } from '../../../../store/hooks';
import CallDetailsDrawer from '../../CallDetailsDrawer/CallDetailsDrawer';
import EmailThreadModal from '../../EmailThreadModal/EmailThreadModal';
import ConversationComposer from '../ConversationComposer/ConversationComposer';
import ConversationList from '../ConversationList/ConversationList';

export default function ConversationTimeline({ contact }: Props) {
    const { classes } = useStyles();
    const queryClient = useQueryClient();
    const convoWrapperRef = React.useRef<HTMLDivElement>(null);
    const [composerHeight, setComposerHeight] = React.useState(150);

    const transcriptDrawerOpen = useAppSelector(
        state => state.conversation.transcriptDrawerChannelItem,
    );
    const emailThreadDrawer = useAppSelector(state => state.contact.emailThreadDrawer);

    //auto resizing of the timeline
    React.useEffect(() => {
        if (!convoWrapperRef.current) return; // wait for the elementRef to be available

        // debounce the update of composerHeight
        const debouncedUpdateComposerHeight = debounce(height => {
            setComposerHeight(height);
        }, 50);

        const resizeObserver = new ResizeObserver(() => {
            const newHeight = convoWrapperRef.current?.offsetHeight || 150;
            debouncedUpdateComposerHeight(newHeight);
        });

        resizeObserver.observe(convoWrapperRef.current);
        return () => {
            resizeObserver.disconnect();
            debouncedUpdateComposerHeight.cancel();
        };
    }, []);

    React.useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ['newAndRecent'] });
    }, [contact.contactId, queryClient]);

    return (
        <Grid container direction="column" wrap="nowrap" className={classes.root}>
            <Grid item xs={true} className={classes.timeline}>
                <ConversationList contact={contact} conversationComposerSize={composerHeight} />
            </Grid>
            <Grid item className={classes.composerWrapper} ref={convoWrapperRef}>
                <ConversationComposer contact={contact} />
            </Grid>
            {transcriptDrawerOpen && <CallDetailsDrawer />}
            {emailThreadDrawer && <EmailThreadModal contact={contact} />}
        </Grid>
    );
}

interface Props {
    contact: Contact;
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.backgroundMainLayout.main,
    },
    timeline: {
        height: '100%',
        overflow: 'hidden',
    },
    composerWrapper: {
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
    },
}));
